// src/pages/LeaderPortal.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoAsset from "../assets/logo.png";

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:      { maxWidth: 1100, margin: "0 auto", padding: "32px 20px", fontFamily: "sans-serif" },
  tabBar:    { display: "flex", gap: 8, marginBottom: 28, flexWrap: "wrap" },
  tab:       (active) => ({
    padding: "10px 22px", borderRadius: 8, border: "none", cursor: "pointer",
    fontWeight: 600, fontSize: 14,
    background: active ? "#00703c" : "#e8f5e9",
    color:      active ? "#fff"    : "#00703c",
  }),
  subTab:    (active) => ({
    padding: "7px 18px", borderRadius: 6, border: "none", cursor: "pointer",
    fontWeight: 600, fontSize: 13,
    background: active ? "#1d4ed8" : "#eff6ff",
    color:      active ? "#fff"    : "#1d4ed8",
  }),
  card:      { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "20px 24px", marginBottom: 16 },
  badge:     (color) => ({ background: color, color: "#fff", borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 600 }),
  btn:       (bg, color = "#fff") => ({
    background: bg, color, border: "none", borderRadius: 6,
    padding: "8px 16px", cursor: "pointer", fontWeight: 600, fontSize: 14,
  }),
  input:     { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 14, boxSizing: "border-box" },
  label:     { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 },
  tblHead:   { background: "#f8fafc" },
  tblTh:     { textAlign: "left", padding: "9px 12px", borderBottom: "1px solid #e2e8f0", fontSize: 13, fontWeight: 700 },
  tblTd:     { padding: "9px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 13 },
};

const statusColor = { pending: "#f59e0b", partially_paid: "#3b82f6", paid: "#22c55e", waived: "#94a3b8", open: "#3b82f6", closed: "#6b7280" };

const STATUS_LABEL = {
  pending:        "Pending",
  partially_paid: "Partly Paid",
  paid:           "Paid",
  waived:         "Waived",
  open:           "Open",
  closed:         "Closed",
};

function Badge({ s }) {
  return <span style={S.badge(statusColor[s] || "#94a3b8")}>{STATUS_LABEL[s] || s}</span>;
}

function Msg({ text }) {
  if (!text) return null;
  const ok = text.startsWith("✅");
  return (
    <div style={{ marginTop: 10, fontSize: 14, padding: "8px 12px", borderRadius: 6,
      background: ok ? "#f0fdf4" : "#fef2f2",
      border: `1px solid ${ok ? "#86efac" : "#fca5a5"}` }}>
      {text}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Members Tab
// ─────────────────────────────────────────────────────────────────────────────
function MembersTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("members/admin/members/")
      .then((r) => { setMembers(r.data.members || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: "#64748b" }}>Loading members…</p>;

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>All Members ({members.length})</h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={S.tblHead}>
            <tr>
              {["Name", "Email", "Phone", "Plan", "Joined", "Paid", "Role"].map((h) => (
                <th key={h} style={S.tblTh}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id}>
                <td style={S.tblTd}>{m.full_name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || "—"}</td>
                <td style={S.tblTd}>{m.email || "—"}</td>
                <td style={S.tblTd}>{m.phone || "—"}</td>
                <td style={S.tblTd}>{m.plan || m.plan_name || "—"}</td>
                <td style={S.tblTd}>{m.joined_at ? m.joined_at.slice(0, 10) : "—"}</td>
                <td style={S.tblTd}>{m.is_paid ? "✅" : "⏳"}</td>
                <td style={S.tblTd}><Badge s={m.role || "member"} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Payments Tab
// ─────────────────────────────────────────────────────────────────────────────
function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    api.get("members/admin/payments/")
      .then((r) => { setPayments(r.data.payments || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: "#64748b" }}>Loading payments…</p>;

  const total = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Payments ({payments.length}) — Total: ${total.toFixed(2)} AUD</h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={S.tblHead}>
            <tr>
              {["Member", "Type", "Amount", "Method", "Status", "Date"].map((h) => (
                <th key={h} style={S.tblTh}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id}>
                <td style={S.tblTd}>{p.member_name}</td>
                <td style={S.tblTd}>{p.payment_type}</td>
                <td style={S.tblTd}>${parseFloat(p.amount).toFixed(2)}</td>
                <td style={S.tblTd}>{p.is_manual ? "🏦 Manual" : "💳 Stripe"}</td>
                <td style={S.tblTd}><Badge s={p.status} /></td>
                <td style={S.tblTd}>{p.created_at ? p.created_at.slice(0, 10) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Welfare Members sub-tab
// ─────────────────────────────────────────────────────────────────────────────
function WelfareMembersTab() {
  const [welfare,      setWelfare]      = useState([]);
  const [welfarePlans, setWelfarePlans] = useState([]);
  const [allMembers,   setAllMembers]   = useState([]);
  const [loading,      setLoading]      = useState(true);

  // Enroll new member form
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollForm, setEnrollForm] = useState({
    member_id: "", welfare_plan_id: "", payment_plan: "full", total_paid: ""
  });
  const [enrollMsg,  setEnrollMsg]  = useState("");
  const [enrolling,  setEnrolling]  = useState(false);

  // Balance / payment panel
  const [editingId,     setEditingId]     = useState(null); // welfare_member_id
  const [adjustForm,    setAdjustForm]    = useState({ total_paid: "" });
  const [adjustMsg,     setAdjustMsg]     = useState("");
  const [adjusting,     setAdjusting]     = useState(false);
  const [payForm,       setPayForm]       = useState({ amount: "" });
  const [payMsg,        setPayMsg]        = useState("");
  const [paying,        setPaying]        = useState(false);
  const [reminderIds,   setReminderIds]   = useState([]);
  const [reminderMsg,   setReminderMsg]   = useState("");
  const [sendingRemind, setSendingRemind] = useState(false);

  // Edit member modal
  const [editMemberModal, setEditMemberModal] = useState(null); // holds { wm, form }
  const [editMemberMsg,   setEditMemberMsg]   = useState("");
  const [editMemberSaving,setEditMemberSaving]= useState(false);
  const [unenrollingId,   setUnenrollingId]   = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, pRes, mRes] = await Promise.allSettled([
        api.get("members/admin/welfare/"),
        api.get("members/welfare-plans/"),
        api.get("members/admin/members/"),
      ]);
      if (wRes.status === "fulfilled") setWelfare(wRes.value.data.welfare_members || []);
      if (pRes.status === "fulfilled") setWelfarePlans(pRes.value.data || []);
      if (mRes.status === "fulfilled") setAllMembers(mRes.value.data.members || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Members not yet enrolled
  const enrolledMemberIds = new Set(welfare.map((w) => w.member_id));
  const unenrolledMembers = allMembers.filter((m) => !enrolledMemberIds.has(m.id));

  const handleEnroll = async (e) => {
    e.preventDefault();
    setEnrolling(true); setEnrollMsg("");
    try {
      await api.post("members/admin/welfare-members/enroll/", {
        member_id      : parseInt(enrollForm.member_id),
        welfare_plan_id: parseInt(enrollForm.welfare_plan_id),
        payment_plan   : enrollForm.payment_plan,
        total_paid     : parseFloat(enrollForm.total_paid || 0),
      });
      setEnrollMsg("✅ Member enrolled successfully.");
      setEnrollForm({ member_id: "", welfare_plan_id: "", payment_plan: "full", total_paid: "" });
      setShowEnroll(false);
      fetchAll();
    } catch (err) {
      setEnrollMsg(`❌ ${err.response?.data?.error || "Failed to enroll."}`);
    } finally { setEnrolling(false); }
  };

  const handleAdjust = async (wm) => {
    setAdjusting(true); setAdjustMsg("");
    try {
      await api.patch(`members/admin/welfare-members/${wm.welfare_member_id}/adjust/`, {
        total_paid: parseFloat(adjustForm.total_paid),
      });
      setAdjustMsg("✅ Balance updated.");
      fetchAll();
    } catch (err) {
      setAdjustMsg(`❌ ${err.response?.data?.error || "Failed to adjust."}`);
    } finally { setAdjusting(false); }
  };

  const handleManualPay = async (wm) => {
    setPaying(true); setPayMsg("");
    try {
      await api.post(`members/admin/welfare-members/${wm.welfare_member_id}/payment/`, {
        amount: parseFloat(payForm.amount),
      });
      setPayMsg("✅ Manual payment recorded.");
      setPayForm({ amount: "" });
      fetchAll();
    } catch (err) {
      setPayMsg(`❌ ${err.response?.data?.error || "Failed to record payment."}`);
    } finally { setPaying(false); }
  };

  const sendReminders = async () => {
    setSendingRemind(true); setReminderMsg("");
    try {
      const body = reminderIds.length > 0 ? { member_ids: reminderIds } : {};
      const r = await api.post("members/admin/welfare-reminders/", body);
      setReminderMsg(`✅ Sent to ${r.data.count} member(s).${r.data.failed.length ? ` Failed: ${r.data.failed.join(", ")}` : ""}`);
    } catch { setReminderMsg("❌ Network error."); }
    finally { setSendingRemind(false); }
  };

  const openEditMember = (wm) => {
    setEditMemberModal({
      wm,
      form: {
        first_name     : wm.first_name || wm.full_name.split(" ")[0] || "",
        last_name      : wm.last_name  || wm.full_name.split(" ").slice(1).join(" ") || "",
        email          : wm.email || "",
        phone          : wm.phone || "",
        date_of_birth  : wm.date_of_birth || "",
        welfare_plan_id: wm.welfare_plan_id || "",
        payment_plan   : wm.payment_plan || "full",
      },
    });
    setEditMemberMsg("");
  };

  const saveEditMember = async () => {
    if (!editMemberModal) return;
    setEditMemberSaving(true); setEditMemberMsg("");
    try {
      await api.patch(
        `members/admin/welfare-members/${editMemberModal.wm.welfare_member_id}/edit/`,
        editMemberModal.form,
      );
      setEditMemberMsg("✅ Member updated.");
      fetchAll();
      setTimeout(() => setEditMemberModal(null), 800);
    } catch (err) {
      setEditMemberMsg(`❌ ${err.response?.data?.error || "Failed to save."}`);
    } finally { setEditMemberSaving(false); }
  };

  const unenrollMember = async (wm) => {
    if (!window.confirm(`Remove ${wm.full_name} from the welfare programme? Their member account will be kept.`)) return;
    setUnenrollingId(wm.welfare_member_id);
    try {
      await api.delete(`members/admin/welfare-members/${wm.welfare_member_id}/unenroll/`);
      fetchAll();
    } catch { alert("Failed to unenroll member."); }
    finally { setUnenrollingId(null); }
  };

  const downloadWelfarePDF = async () => {
    try {
      const r = await api.get("members/admin/welfare-report/");
      const report = r.data;
      const doc = new jsPDF();

      const renderPDF = (logoDataUrl) => {
        if (logoDataUrl) doc.addImage(logoDataUrl, "PNG", 14, 8, 22, 22);
        doc.setFontSize(18); doc.setTextColor(0, 112, 60);
        doc.text("Marakwet Community in Australia", logoDataUrl ? 40 : 14, 16);
        doc.setFontSize(10); doc.setTextColor(80);
        doc.text("Welfare Report — " + report.generated_at, logoDataUrl ? 40 : 14, 22);
        doc.setFontSize(12); doc.setTextColor(30);
        doc.text(`Members: ${report.total_members}   Total Collected: $${report.total_collected.toFixed(2)}   Outstanding: $${report.total_outstanding.toFixed(2)}`, 14, 34);

        autoTable(doc, {
          startY: 40,
          head: [["#", "Name", "Plan", "Payment Plan", "Total Paid", "Remaining", "Instalments", "Status"]],
          body: report.members.map((m, i) => [
            i + 1, m.name, m.welfare_plan, m.payment_plan,
            `$${m.total_paid.toFixed(2)}`, `$${m.remaining.toFixed(2)}`,
            `${m.paid_installments}/${m.total_installments}`,
            m.is_fully_paid ? "Fully Paid" : "In Progress",
          ]),
          headStyles: { fillColor: [0, 112, 60], textColor: 255, fontSize: 9 },
          bodyStyles: { fontSize: 8 },
          alternateRowStyles: { fillColor: [240, 247, 240] },
        });

        if (report.reimbursements.length > 0) {
          doc.addPage();
          doc.setFontSize(13); doc.setTextColor(0, 112, 60);
          doc.text("Welfare Reimbursements", 14, 20);
          autoTable(doc, {
            startY: 26,
            head: [["Beneficiary", "Reason", "Total", "Contrib/Member", "Collected", "Status", "Date"]],
            body: report.reimbursements.map((r) => [
              r.beneficiary, r.reason, `$${r.total_amount.toFixed(2)}`,
              `$${r.contribution.toFixed(2)}`,
              `$${r.total_collected.toFixed(2)} (${r.paid_count}/${r.total_count})`,
              r.status, r.created_at,
            ]),
            headStyles: { fillColor: [0, 112, 60], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8 },
            alternateRowStyles: { fillColor: [240, 247, 240] },
          });
        }

        doc.save(`MCIA_Welfare_Report_${report.generated_at.slice(0, 10)}.pdf`);
      };

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        renderPDF(canvas.toDataURL("image/png"));
      };
      img.onerror = () => renderPDF(null);
      img.src = logoAsset;
    } catch { alert("Failed to generate report."); }
  };

  if (loading) return <p style={{ color: "#64748b" }}>Loading welfare members…</p>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0 }}>Welfare Members ({welfare.length})</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={downloadWelfarePDF} style={S.btn("#00703c")}>⬇ Download Report</button>
          <button onClick={sendReminders} disabled={sendingRemind} style={S.btn("#f59e0b")}>
            {sendingRemind ? "Sending…" : "📩 Send Reminders"}
          </button>
          <button onClick={() => { setShowEnroll(!showEnroll); setEnrollMsg(""); }}
            style={S.btn("#1d4ed8")}>
            {showEnroll ? "✕ Cancel" : "+ Enroll Member"}
          </button>
        </div>
      </div>

      <Msg text={reminderMsg} />
      <Msg text={enrollMsg} />

      {/* Enroll form */}
      {showEnroll && (
        <form onSubmit={handleEnroll}
          style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <h4 style={{ marginTop: 0 }}>Enroll Existing Member in Welfare</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={S.label}>Member *</label>
              <select required value={enrollForm.member_id}
                onChange={(e) => setEnrollForm({ ...enrollForm, member_id: e.target.value })}
                style={S.input}>
                <option value="">— Select member —</option>
                {unenrolledMembers.map((m) => (
                  <option key={m.id} value={m.id}>{m.full_name} — {m.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>Welfare Plan *</label>
              <select required value={enrollForm.welfare_plan_id}
                onChange={(e) => setEnrollForm({ ...enrollForm, welfare_plan_id: e.target.value })}
                style={S.input}>
                <option value="">— Select plan —</option>
                {welfarePlans.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — ${p.total_amount}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>Payment Plan *</label>
              <select value={enrollForm.payment_plan}
                onChange={(e) => setEnrollForm({ ...enrollForm, payment_plan: e.target.value })}
                style={S.input}>
                <option value="full">Pay in Full</option>
                <option value="weekly">Weekly (12 payments)</option>
                <option value="fortnightly">Fortnightly (6 payments)</option>
                <option value="monthly">Monthly (3 payments)</option>
              </select>
            </div>
            <div>
              <label style={S.label}>Amount Already Paid ($AUD)</label>
              <input type="number" step="0.01" min="0" value={enrollForm.total_paid}
                onChange={(e) => setEnrollForm({ ...enrollForm, total_paid: e.target.value })}
                placeholder="0.00 — leave blank if none yet"
                style={S.input} />
            </div>
          </div>
          <button type="submit" disabled={enrolling}
            style={{ ...S.btn("#1d4ed8"), marginTop: 14 }}>
            {enrolling ? "Enrolling…" : "Enroll Member"}
          </button>
        </form>
      )}

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={S.tblHead}>
            <tr>
              {["Member", "Plan", "Payment Plan", "Total Paid", "Remaining", "Instalments", "Status", "Actions"].map((h) => (
                <th key={h} style={S.tblTh}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {welfare.map((w) => (
              <React.Fragment key={w.welfare_member_id || w.member_id}>
                <tr style={{ background: editingId === w.welfare_member_id ? "#f0f9ff" : undefined }}>
                  <td style={S.tblTd}>
                    <div style={{ fontWeight: 600 }}>{w.full_name}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{w.email}</div>
                  </td>
                  <td style={S.tblTd}>{w.welfare_plan}</td>
                  <td style={S.tblTd}>{w.payment_plan}</td>
                  <td style={S.tblTd}>${parseFloat(w.total_paid || 0).toFixed(2)}</td>
                  <td style={S.tblTd}>${parseFloat(w.remaining || 0).toFixed(2)}</td>
                  <td style={S.tblTd}>{w.paid_installments}/{w.total_installments}</td>
                  <td style={S.tblTd}>{w.is_fully_paid ? <Badge s="paid" /> : <Badge s="pending" />}</td>
                  <td style={S.tblTd}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      <button
                        onClick={() => {
                          if (editingId === w.welfare_member_id) {
                            setEditingId(null);
                          } else {
                            setEditingId(w.welfare_member_id);
                            setAdjustForm({ total_paid: w.total_paid });
                            setPayForm({ amount: "" });
                            setAdjustMsg(""); setPayMsg("");
                          }
                        }}
                        style={{ ...S.btn(editingId === w.welfare_member_id ? "#6b7280" : "#1d4ed8"), padding: "4px 10px", fontSize: 12 }}>
                        {editingId === w.welfare_member_id ? "Close" : "Manage"}
                      </button>
                      <button
                        onClick={() => openEditMember(w)}
                        style={{ ...S.btn("#f59e0b"), padding: "4px 10px", fontSize: 12 }}
                        title="Edit member details">
                        ✏️ Edit
                      </button>
                      <button
                        disabled={unenrollingId === w.welfare_member_id}
                        onClick={() => unenrollMember(w)}
                        style={{ ...S.btn("#fee2e2", "#ef4444"), border: "1px solid #fca5a5", padding: "4px 10px", fontSize: 12 }}
                        title="Remove from welfare programme">
                        {unenrollingId === w.welfare_member_id ? "…" : "🗑"}
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Inline management panel */}
                {editingId === w.welfare_member_id && (
                  <tr>
                    <td colSpan={8} style={{ padding: "12px 18px", background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                        {/* Adjust balance */}
                        <div>
                          <h5 style={{ margin: "0 0 8px", color: "#1d4ed8" }}>⚖️ Adjust Total Balance</h5>
                          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#64748b" }}>
                            Override the total amount paid (for reconciling historical records).
                          </p>
                          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                            <div style={{ flex: 1 }}>
                              <label style={S.label}>New Total Paid ($AUD)</label>
                              <input type="number" step="0.01" min="0" value={adjustForm.total_paid}
                                onChange={(e) => setAdjustForm({ total_paid: e.target.value })}
                                style={S.input} />
                            </div>
                            <button disabled={adjusting} onClick={() => handleAdjust(w)}
                              style={{ ...S.btn("#7c3aed"), whiteSpace: "nowrap" }}>
                              {adjusting ? "Saving…" : "Set Balance"}
                            </button>
                          </div>
                          <Msg text={adjustMsg} />
                        </div>

                        {/* Record manual payment */}
                        <div>
                          <h5 style={{ margin: "0 0 8px", color: "#00703c" }}>💵 Record Manual Payment</h5>
                          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#64748b" }}>
                            Record a cash or bank-transfer payment (adds to their running total).
                          </p>
                          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                            <div style={{ flex: 1 }}>
                              <label style={S.label}>Payment Amount ($AUD)</label>
                              <input type="number" step="0.01" min="0.01" value={payForm.amount}
                                onChange={(e) => setPayForm({ amount: e.target.value })}
                                placeholder="e.g. 100.00"
                                style={S.input} />
                            </div>
                            <button disabled={paying} onClick={() => handleManualPay(w)}
                              style={{ ...S.btn("#00703c"), whiteSpace: "nowrap" }}>
                              {paying ? "Recording…" : "Record"}
                            </button>
                          </div>
                          <Msg text={payMsg} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Edit Member Modal ── */}
      {editMemberModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }} onClick={() => setEditMemberModal(null)}>
          <div style={{
            background: "#fff", borderRadius: 12, padding: 28, width: "100%", maxWidth: 520,
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <h4 style={{ margin: 0 }}>✏️ Edit Welfare Member</h4>
              <button onClick={() => setEditMemberModal(null)}
                style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { key: "first_name",    label: "First Name" },
                { key: "last_name",     label: "Last Name" },
                { key: "email",         label: "Email", type: "email" },
                { key: "phone",         label: "Phone" },
                { key: "date_of_birth", label: "Date of Birth", type: "date" },
              ].map(({ key, label, type = "text" }) => (
                <div key={key} style={key === "email" ? { gridColumn: "1 / -1" } : {}}>
                  <label style={S.label}>{label}</label>
                  <input
                    type={type}
                    value={editMemberModal.form[key] || ""}
                    onChange={(e) => setEditMemberModal({
                      ...editMemberModal,
                      form: { ...editMemberModal.form, [key]: e.target.value },
                    })}
                    style={S.input}
                  />
                </div>
              ))}
              <div>
                <label style={S.label}>Welfare Plan</label>
                <select
                  value={editMemberModal.form.welfare_plan_id || ""}
                  onChange={(e) => setEditMemberModal({
                    ...editMemberModal,
                    form: { ...editMemberModal.form, welfare_plan_id: e.target.value },
                  })}
                  style={S.input}>
                  {welfarePlans.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={S.label}>Payment Plan</label>
                <select
                  value={editMemberModal.form.payment_plan || "full"}
                  onChange={(e) => setEditMemberModal({
                    ...editMemberModal,
                    form: { ...editMemberModal.form, payment_plan: e.target.value },
                  })}
                  style={S.input}>
                  <option value="full">Pay in Full</option>
                  <option value="weekly">Weekly (12 payments)</option>
                  <option value="fortnightly">Fortnightly (6 payments)</option>
                  <option value="monthly">Monthly (3 payments)</option>
                </select>
              </div>
            </div>
            <Msg text={editMemberMsg} />
            <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
              <button onClick={() => setEditMemberModal(null)}
                style={{ ...S.btn("#f1f5f9", "#475569"), border: "1px solid #cbd5e1" }}>Cancel</button>
              <button onClick={saveEditMember} disabled={editMemberSaving}
                style={S.btn("#00703c")}>
                {editMemberSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reimbursements sub-tab
// ─────────────────────────────────────────────────────────────────────────────
function ReimbursementsTab() {
  const [reimbursements, setReimbursements] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [expandedId,     setExpandedId]     = useState(null);
  const [members,        setMembers]        = useState([]);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [form,       setForm]       = useState({
    beneficiary_ids: [], reason: "bereavement", description: "",
    total_amount: "", contribution_per_member: "", due_date: "",
  });
  const [createMsg, setCreateMsg] = useState("");
  const [creating,  setCreating]  = useState(false);

  // Edit form
  const [editingId,   setEditingId]   = useState(null);
  const [editForm,    setEditForm]    = useState({});
  const [editMsg,     setEditMsg]     = useState("");
  const [editSaving,  setEditSaving]  = useState(false);

  // Delete
  const [deletingId,  setDeletingId]  = useState(null);

  // Reminders
  const [reminderMsg,     setReminderMsg]     = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);

  const [updatingId, setUpdatingId] = useState(null);
  // Inline edit state for a contribution row: { id, amount }
  const [editingContrib, setEditingContrib] = useState(null);
  const [deletingContribId, setDeletingContribId] = useState(null);
  // Per-contribution amount_paid inputs (before save): { [id]: "200" }
  const [amtPaidInputs, setAmtPaidInputs] = useState({});

  const fetchAll = useCallback(() => {
    setLoading(true);
    api.get("members/admin/reimbursements/")
      .then((r) => { setReimbursements(r.data.reimbursements || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAll();
    api.get("members/admin/members/")
      .then((r) => setMembers(r.data.members || []))
      .catch(() => {});
  }, [fetchAll]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.beneficiary_ids || form.beneficiary_ids.length === 0) {
      setCreateMsg("❌ Please select at least one beneficiary.");
      return;
    }
    setCreating(true); setCreateMsg("");
    try {
      const r = await api.post("members/admin/reimbursements/create/", {
        beneficiary_ids         : form.beneficiary_ids.map(Number),
        reason                  : form.reason,
        description             : form.description,
        total_amount            : parseFloat(form.total_amount),
        contribution_per_member : parseFloat(form.contribution_per_member),
        due_date                : form.due_date || null,
      });
      const names = (r.data.beneficiaries || []).join(", ");
      setCreateMsg(`✅ Created for ${names}. ${r.data.contributors_notified}/${r.data.total_contributors} contributors notified.`);
      setForm({ beneficiary_ids: [], reason: "bereavement", description: "", total_amount: "", contribution_per_member: "", due_date: "" });
      setShowCreate(false);
      fetchAll();
    } catch (err) {
      setCreateMsg(`❌ ${err.response?.data?.error || "Failed to create."}`);
    } finally { setCreating(false); }
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setEditForm({
      reason                  : r.reason_key || r.reason,
      description             : r.description,
      total_amount            : r.total_amount,
      contribution_per_member : r.contribution_amount,
      due_date                : r.due_date || "",
      status                  : r.status,
    });
    setEditMsg("");
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true); setEditMsg("");
    try {
      await api.patch(`members/admin/reimbursements/${editingId}/edit/`, {
        reason                  : editForm.reason,
        description             : editForm.description,
        total_amount            : parseFloat(editForm.total_amount),
        contribution_per_member : parseFloat(editForm.contribution_per_member),
        due_date                : editForm.due_date || null,
        status                  : editForm.status,
      });
      setEditMsg("✅ Saved.");
      setEditingId(null);
      fetchAll();
    } catch (err) {
      setEditMsg(`❌ ${err.response?.data?.error || "Failed to save."}`);
    } finally { setEditSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this reimbursement and all its contributions? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await api.delete(`members/admin/reimbursements/${id}/delete/`);
      fetchAll();
    } catch { alert("Failed to delete reimbursement."); }
    finally { setDeletingId(null); }
  };

  // Save a custom amount_paid value — status derived server-side
  const saveAmountPaid = async (c) => {
    const val = amtPaidInputs[c.id];
    if (val === undefined || val === "") return;
    setUpdatingId(c.id);
    try {
      await api.patch(`members/admin/reimbursements/contributions/${c.id}/`, {
        amount_paid: parseFloat(val),
      });
      setAmtPaidInputs((prev) => { const n = { ...prev }; delete n[c.id]; return n; });
      fetchAll();
    } catch { alert("Failed to update payment."); }
    finally { setUpdatingId(null); }
  };

  // Legacy status-only update — used for Undo (→ pending)
  const updateContribution = async (id, status) => {
    setUpdatingId(id);
    try {
      await api.patch(`members/admin/reimbursements/contributions/${id}/`, { status });
      fetchAll();
    } finally { setUpdatingId(null); }
  };

  const saveContribEdit = async () => {
    if (!editingContrib) return;
    setUpdatingId(editingContrib.id);
    try {
      await api.patch(`members/admin/reimbursements/contributions/${editingContrib.id}/`, {
        amount: editingContrib.amount,
      });
      setEditingContrib(null);
      fetchAll();
    } catch { alert("Failed to update amount."); }
    finally { setUpdatingId(null); }
  };

  const deleteContrib = async (id) => {
    if (!window.confirm("Remove this member from the reimbursement?")) return;
    setDeletingContribId(id);
    try {
      await api.delete(`members/admin/reimbursements/contributions/${id}/delete/`);
      fetchAll();
    } catch { alert("Failed to delete contribution."); }
    finally { setDeletingContribId(null); }
  };

  const sendReminders = async (reimbursementId) => {
    setSendingReminder(true); setReminderMsg("");
    try {
      const body = reimbursementId ? { reimbursement_id: reimbursementId } : {};
      const r = await api.post("members/admin/reimbursements/send-reminders/", body);
      setReminderMsg(`✅ Sent to ${r.data.count} member(s).${r.data.failed.length ? ` Failed: ${r.data.failed.join(", ")}` : ""}`);
    } catch { setReminderMsg("❌ Network error."); }
    finally { setSendingReminder(false); }
  };

  const downloadReimbursementPDF = async () => {
    try {
      const res = await api.get("members/admin/reimbursements/");
      const reimbs = res.data.reimbursements || [];

      // Also fetch welfare member totals for principal column
      const wmRes = await api.get("members/admin/welfare-report/");
      const wmMap = {};
      (wmRes.data.members || []).forEach((m) => { wmMap[m.email] = m; });

      const doc = new jsPDF({ orientation: "landscape" });

      const renderPDF = (logoDataUrl) => {
        if (logoDataUrl) doc.addImage(logoDataUrl, "PNG", 14, 6, 18, 18);
        doc.setFontSize(16); doc.setTextColor(0, 112, 60);
        doc.text("Marakwet Community in Australia", logoDataUrl ? 36 : 14, 13);
        doc.setFontSize(9); doc.setTextColor(80);
        doc.text("MCIA WELFARE CONTRIBUTION AND REIMBURSEMENT REGISTER", logoDataUrl ? 36 : 14, 19);
        doc.text(`Generated: ${new Date().toLocaleString("en-AU")}`, logoDataUrl ? 36 : 14, 24);

        // Build rows — one per reimbursement contribution with running totals per member
        // Group contributions by member across all reimbursements
        const memberMap = {};
        reimbs.forEach((r) => {
          (r.contributions || []).forEach((c) => {
            if (!memberMap[c.member]) {
              memberMap[c.member] = {
                name: c.member,
                email: c.email,
                contributions: [],
              };
            }
            memberMap[c.member].contributions.push({
              reimbursementId: r.id,
              reason: r.reason,
              beneficiary: r.beneficiary,
              amount: c.amount,
              amount_paid: c.amount_paid,
              status: c.status,
              paid_at: c.paid_at,
            });
          });
        });

        const rows = [];
        let rowNum = 0;
        Object.values(memberMap).forEach((m) => {
          rowNum += 1;
          const wm = wmMap[m.email];
          const principalTotal  = wm ? wm.plan_total  : "—";
          const principalPaid   = wm ? wm.total_paid   : "—";
          const principalBalance = wm ? wm.remaining   : "—";
          const principalStatus = wm
            ? (wm.is_fully_paid ? "Cleared" : `Bal $${parseFloat(principalBalance).toFixed(2)}`)
            : "—";
          const principalAmt = wm
            ? `$${parseFloat(principalTotal).toFixed(2)}`
            : "—";

          // Reimbursement contributions for this member
          const reimbAmounts = m.contributions.map((c) => {
            const owed = parseFloat(c.amount || 0);
            const paid = parseFloat(c.amount_paid || 0);
            const statusLabel = c.status === "paid" ? "✓" : c.status === "partially_paid" ? `$${paid.toFixed(2)} paid` : "⏳";
            return `$${owed.toFixed(2)} (${c.beneficiary} — ${c.reason} ${statusLabel})`;
          }).join(", ");

          const paidContribs      = m.contributions.filter((c) => c.status === "paid");
          const pendingContribs   = m.contributions.filter((c) => c.status !== "paid");
          const totalReimb        = m.contributions.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
          const collectedReimb    = m.contributions.reduce((s, c) => s + parseFloat(c.amount_paid || 0), 0);
          const outstandingReimb  = Math.max(totalReimb - collectedReimb, 0);

          const reimbStatus   = outstandingReimb <= 0 ? "Cleared" : `Bal $${outstandingReimb.toFixed(2)}`;
          const totalOutstand = outstandingReimb > 0
            ? `$${outstandingReimb.toFixed(2)}`
            : "Nil";

          rows.push([
            rowNum,
            m.name,
            principalAmt,
            principalStatus,
            reimbAmounts || "—",
            reimbStatus,
            totalOutstand,
          ]);
        });

        autoTable(doc, {
          startY: 30,
          head: [["#", "Member", "Principal Amount", "Principal Balance", "Reimbursement Amount", "Balance for Reimbursement", "Total Outstanding"]],
          body: rows,
          headStyles: { fillColor: [0, 112, 60], textColor: 255, fontSize: 8, fontStyle: "bold" },
          bodyStyles: { fontSize: 7.5 },
          alternateRowStyles: { fillColor: [240, 247, 240] },
          columnStyles: {
            0: { cellWidth: 8 },
            1: { cellWidth: 32 },
            2: { cellWidth: 26 },
            3: { cellWidth: 26 },
            4: { cellWidth: 90 },
            5: { cellWidth: 30 },
            6: { cellWidth: 30 },
          },
          margin: { left: 10, right: 10 },
        });

        doc.save(`MCIA_Welfare_Reimbursement_Register_${new Date().toISOString().slice(0,10)}.pdf`);
      };

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        renderPDF(canvas.toDataURL("image/png"));
      };
      img.onerror = () => renderPDF(null);
      img.src = logoAsset;
    } catch (err) { alert("Failed to generate report: " + (err.message || "unknown error")); }
  };

  const REASON_OPTIONS = [
    { value: "bereavement", label: "Bereavement" },
    { value: "illness",     label: "Illness" },
    { value: "emergency",   label: "Emergency" },
    { value: "other",       label: "Other" },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0 }}>Welfare Reimbursements</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => sendReminders(null)} disabled={sendingReminder} style={S.btn("#f59e0b")}>
            {sendingReminder ? "Sending…" : "📩 Send Reimbursement Reminders"}
          </button>
          <button onClick={downloadReimbursementPDF} style={S.btn("#00703c")}>
            ⬇ Download Report
          </button>
          <button onClick={() => { setShowCreate(!showCreate); setCreateMsg(""); }}
            style={S.btn("#1d4ed8")}>
            {showCreate ? "✕ Cancel" : "+ New Reimbursement"}
          </button>
        </div>
      </div>

      <Msg text={reminderMsg} />
      <Msg text={createMsg && !showCreate ? createMsg : ""} />

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate}
          style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 20, marginBottom: 24 }}>
          <h4 style={{ marginTop: 0 }}>New Reimbursement</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={S.label}>
                Beneficiaries * <span style={{ fontWeight: 400, color: "#64748b", fontSize: 12 }}>(hold Ctrl/Cmd to select multiple)</span>
              </label>
              <select
                multiple
                required
                size={Math.min(members.length + 1, 6)}
                value={form.beneficiary_ids}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                  setForm({ ...form, beneficiary_ids: selected });
                }}
                style={{ ...S.input, height: "auto", minHeight: 90 }}>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.full_name || `${m.first_name || ""} ${m.last_name || ""}`.trim()}
                  </option>
                ))}
              </select>
              {form.beneficiary_ids.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 12, color: "#00703c" }}>
                  ✓ {form.beneficiary_ids.length} beneficiar{form.beneficiary_ids.length === 1 ? "y" : "ies"} selected
                </div>
              )}
            </div>
            <div>
              <label style={S.label}>Reason *</label>
              <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} style={S.input}>
                {REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Total Amount (AUD) *</label>
              <input type="number" step="0.01" min="0" required value={form.total_amount}
                onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                placeholder="e.g. 500.00" style={S.input} />
            </div>
            <div>
              <label style={S.label}>Contribution per Member (AUD) *</label>
              <input type="number" step="0.01" min="0" required value={form.contribution_per_member}
                onChange={(e) => setForm({ ...form, contribution_per_member: e.target.value })}
                placeholder="e.g. 20.00" style={S.input} />
            </div>
            <div>
              <label style={S.label}>Due Date</label>
              <input type="date" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })} style={S.input} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={S.label}>Description / Notes</label>
              <textarea rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional details…"
                style={{ ...S.input, resize: "vertical" }} />
            </div>
          </div>
          <Msg text={createMsg} />
          <button type="submit" disabled={creating} style={{ ...S.btn("#1d4ed8"), marginTop: 14 }}>
            {creating ? "Creating…" : "Create & Notify Members"}
          </button>
        </form>
      )}

      {/* Edit form modal-style overlay */}
      {editingId && (
        <div style={{ background: "#fffbeb", border: "2px solid #fbbf24", borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <h4 style={{ marginTop: 0, color: "#92400e" }}>✏️ Edit Reimbursement #{editingId}</h4>
          <form onSubmit={handleEdit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={S.label}>Reason</label>
                <select value={editForm.reason}
                  onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} style={S.input}>
                  {REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Status</label>
                <select value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} style={S.input}>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div>
                <label style={S.label}>Total Amount (AUD)</label>
                <input type="number" step="0.01" min="0" value={editForm.total_amount}
                  onChange={(e) => setEditForm({ ...editForm, total_amount: e.target.value })} style={S.input} />
              </div>
              <div>
                <label style={S.label}>Contribution per Member (AUD)</label>
                <input type="number" step="0.01" min="0" value={editForm.contribution_per_member}
                  onChange={(e) => setEditForm({ ...editForm, contribution_per_member: e.target.value })} style={S.input} />
              </div>
              <div>
                <label style={S.label}>Due Date</label>
                <input type="date" value={editForm.due_date}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} style={S.input} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={S.label}>Description / Notes</label>
                <textarea rows={2} value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  style={{ ...S.input, resize: "vertical" }} />
              </div>
            </div>
            <Msg text={editMsg} />
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button type="submit" disabled={editSaving} style={S.btn("#00703c")}>
                {editSaving ? "Saving…" : "Save Changes"}
              </button>
              <button type="button" onClick={() => setEditingId(null)}
                style={{ ...S.btn("#f1f5f9", "#475569"), border: "1px solid #cbd5e1" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p style={{ color: "#64748b" }}>Loading…</p>
      ) : reimbursements.length === 0 ? (
        <p style={{ color: "#64748b" }}>No reimbursements yet.</p>
      ) : reimbursements.map((r) => (
        <div key={r.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            {/* Clickable summary area */}
            <div onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
              style={{ flex: 1, cursor: "pointer" }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{r.beneficiary}</span>
              <span style={{ marginLeft: 10, color: "#64748b", fontSize: 13 }}>{r.reason}</span>
              {r.description && <span style={{ marginLeft: 8, color: "#94a3b8", fontSize: 12 }}>— {r.description.slice(0, 60)}{r.description.length > 60 ? "…" : ""}</span>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Badge s={r.status} />
              <span style={{ fontSize: 13, color: "#64748b" }}>{r.paid_contributions}/{r.paid_contributions + r.pending_contributions} paid</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>${r.total_collected.toFixed(2)} / ${r.total_amount.toFixed(2)}</span>
              <button
                onClick={(e) => { e.stopPropagation(); sendReminders(r.id); }}
                disabled={sendingReminder}
                title="Send reminder emails to members with pending contributions for this reimbursement"
                style={{ ...S.btn("#f59e0b"), padding: "4px 10px", fontSize: 12 }}>
                {sendingReminder ? "…" : "📩 Remind"}
              </button>
              <button
                onClick={() => openEdit(r)}
                style={{ ...S.btn("#f59e0b"), padding: "4px 10px", fontSize: 12 }}>
                ✏️ Edit
              </button>
              <button
                disabled={deletingId === r.id}
                onClick={() => handleDelete(r.id)}
                style={{ ...S.btn("#ef4444"), padding: "4px 10px", fontSize: 12 }}>
                {deletingId === r.id ? "…" : "🗑 Delete"}
              </button>
              <span onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                style={{ fontSize: 18, color: "#94a3b8", cursor: "pointer" }}>
                {expandedId === r.id ? "▲" : "▼"}
              </span>
            </div>
          </div>

          {expandedId === r.id && (
            <div style={{ borderTop: "1px solid #f1f5f9", padding: "0 18px 16px" }}>
              <div style={{ display: "flex", gap: 16, padding: "10px 0", fontSize: 13, color: "#64748b" }}>
                <span>Created: {r.created_at}</span>
                {r.due_date && <span>Due: {r.due_date}</span>}
                <span>Per member: ${r.contribution_amount.toFixed(2)}</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead style={S.tblHead}>
                    <tr>
                      {["Member", "Email", "Owed", "Amt Paid", "Remaining", "Status", "Action"].map((h) => (
                        <th key={h} style={S.tblTh}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {r.contributions.map((c) => {
                      const owed      = parseFloat(c.amount     || 0);
                      const amtPaid   = parseFloat(c.amount_paid || 0);
                      const remaining = Math.max(owed - amtPaid, 0);
                      const inputVal  = amtPaidInputs[c.id] !== undefined ? amtPaidInputs[c.id] : String(amtPaid.toFixed(2));
                      return (
                        <tr key={c.id}>
                          <td style={S.tblTd}>{c.member}</td>
                          <td style={S.tblTd}>{c.email}</td>

                          {/* Owed (editable) */}
                          <td style={S.tblTd}>
                            {editingContrib?.id === c.id ? (
                              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={editingContrib.amount}
                                  onChange={(e) => setEditingContrib({ ...editingContrib, amount: e.target.value })}
                                  style={{ ...S.input, width: 80, fontSize: 12, padding: "2px 6px" }}
                                />
                                <button onClick={saveContribEdit} disabled={updatingId === c.id}
                                  style={{ ...S.btn("#22c55e"), padding: "2px 8px", fontSize: 11 }}>✓</button>
                                <button onClick={() => setEditingContrib(null)}
                                  style={{ ...S.btn("#94a3b8"), padding: "2px 8px", fontSize: 11 }}>✕</button>
                              </div>
                            ) : (
                              <span title="Click to edit owed amount"
                                onClick={() => setEditingContrib({ id: c.id, amount: owed })}
                                style={{ cursor: "pointer", borderBottom: "1px dashed #94a3b8" }}>
                                ${owed.toFixed(2)}
                              </span>
                            )}
                          </td>

                          {/* Amount Paid — editable input */}
                          <td style={S.tblTd}>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              <input
                                type="number" min="0" step="0.01"
                                max={owed}
                                value={inputVal}
                                onChange={(e) => setAmtPaidInputs((prev) => ({ ...prev, [c.id]: e.target.value }))}
                                style={{ ...S.input, width: 80, fontSize: 12, padding: "2px 6px",
                                  borderColor: amtPaidInputs[c.id] !== undefined ? "#3b82f6" : "#cbd5e1" }}
                              />
                              {amtPaidInputs[c.id] !== undefined && (
                                <>
                                  <button onClick={() => saveAmountPaid(c)} disabled={updatingId === c.id}
                                    style={{ ...S.btn("#22c55e"), padding: "2px 8px", fontSize: 11 }}>✓</button>
                                  <button onClick={() => setAmtPaidInputs((prev) => { const n={...prev}; delete n[c.id]; return n; })}
                                    style={{ ...S.btn("#94a3b8"), padding: "2px 8px", fontSize: 11 }}>✕</button>
                                </>
                              )}
                            </div>
                          </td>

                          {/* Remaining */}
                          <td style={{ ...S.tblTd, color: remaining > 0 ? "#ef4444" : "#22c55e", fontWeight: 600 }}>
                            {remaining > 0 ? `$${remaining.toFixed(2)}` : "—"}
                          </td>

                          <td style={S.tblTd}><Badge s={c.status} /></td>

                          {/* Action */}
                          <td style={S.tblTd}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {c.status !== "pending" && (
                                <button disabled={updatingId === c.id}
                                  onClick={() => updateContribution(c.id, "pending")}
                                  style={{ ...S.btn("#f1f5f9", "#475569"), border: "1px solid #cbd5e1", padding: "3px 10px", fontSize: 12 }}>
                                  Undo
                                </button>
                              )}
                              <button
                                disabled={deletingContribId === c.id}
                                onClick={() => deleteContrib(c.id)}
                                style={{ ...S.btn("#fee2e2", "#ef4444"), border: "1px solid #fca5a5", padding: "3px 10px", fontSize: 11 }}>
                                {deletingContribId === c.id ? "…" : "🗑 Remove"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Event Attendees Tab
// ─────────────────────────────────────────────────────────────────────────────
function EventAttendeesTab() {
  const [report,   setReport]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("members/event-attendees/")
      .then((r) => { setReport(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const bookerAttendees = (ticket) =>
    ticket.attendees && ticket.attendees.length > 0
      ? ticket.attendees
      : [{ first_name: ticket.booker_name.split(" ")[0], last_name: ticket.booker_name.split(" ").slice(1).join(" "), email: ticket.booker_email }];

  const downloadEventPDF = (event) => {
    const doc = new jsPDF();

    const renderPDF = (logoDataUrl) => {
      if (logoDataUrl) doc.addImage(logoDataUrl, "PNG", 14, 8, 22, 22);
      doc.setFontSize(18); doc.setTextColor(0, 112, 60);
      doc.text("Marakwet Community in Australia", logoDataUrl ? 40 : 14, 16);
      doc.setFontSize(10); doc.setTextColor(80);
      doc.text("marakwetcommunityinaustralia.org", logoDataUrl ? 40 : 14, 22);
      doc.setFontSize(13); doc.setTextColor(30, 30, 30);
      doc.text(`Event: ${event.event_title}`, 14, 36);
      doc.setFontSize(10); doc.setTextColor(100);
      doc.text(`Date: ${event.event_date}   Location: ${event.event_location}`, 14, 43);
      doc.text(`Tickets Sold: ${event.total_tickets_sold}   Revenue: $${event.total_revenue.toFixed(2)} AUD`, 14, 49);
      doc.text(`Report generated: ${new Date().toLocaleString()}`, 14, 55);

      const rows = [];
      let globalRow = 0;
      event.tickets.forEach((ticket) => {
        bookerAttendees(ticket).forEach((a, i) => {
          globalRow += 1;
          rows.push([
            globalRow,
            i === 0 ? ticket.ticket_reference : "",
            i === 0 ? (ticket.package || "Individual") : "",
            `${a.first_name} ${a.last_name}`,
            a.email || "—",
            i === 0 ? ticket.booker_phone || "—" : "",
            i === 0 ? `$${ticket.total_amount.toFixed(2)}` : "",
            i === 0 ? ticket.purchased_at : "",
          ]);
        });
      });

      autoTable(doc, {
        startY: 61,
        head: [["#", "Reference", "Package", "Attendee", "Email", "Phone", "Amount", "Purchased"]],
        body: rows,
        headStyles: { fillColor: [0, 112, 60], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [240, 247, 240] },
        columnStyles: { 0:{cellWidth:8},1:{cellWidth:24},2:{cellWidth:20},3:{cellWidth:33},4:{cellWidth:38},5:{cellWidth:20},6:{cellWidth:16},7:{cellWidth:24} },
      });
      doc.save(`MCIA_${event.event_title.replace(/\s+/g, "_")}_Attendees.pdf`);
    };

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      renderPDF(canvas.toDataURL("image/png"));
    };
    img.onerror = () => renderPDF(null);
    img.src = logoAsset;
  };

  if (loading) return <p style={{ color: "#64748b" }}>Loading attendees report…</p>;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const upcomingEvents = report.filter((e) => new Date(e.event_date) >= today);
  const pastEvents     = report.filter((e) => new Date(e.event_date) <  today);
  const totalRevenue = report.reduce((s, e) => s + e.total_revenue, 0);
  const totalTickets = report.reduce((s, e) => s + e.total_tickets_sold, 0);

  const renderEvent = (event) => (
    <div key={event.event_id} style={{ ...S.card, padding: 0, overflow: "hidden" }}>
      <div onClick={() => toggle(event.event_id)}
        style={{ padding: "14px 18px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 700 }}>{event.event_title}</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>📅 {event.event_date} &nbsp; 📍 {event.event_location}</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>🎟 {event.total_tickets_sold} tickets{event.is_paid_event ? ` · $${event.total_revenue.toFixed(2)} AUD` : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {event.tickets.length > 0 && (
            <button onClick={(e) => { e.stopPropagation(); downloadEventPDF(event); }} style={S.btn("#00703c")}>⬇ PDF</button>
          )}
          <span style={{ color: "#94a3b8" }}>{expanded[event.event_id] ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded[event.event_id] && (
        <div style={{ borderTop: "1px solid #f1f5f9", padding: "0 18px 16px" }}>
          {event.tickets.length === 0 ? (
            <p style={{ color: "#64748b", fontSize: 13 }}>No tickets purchased for this event.</p>
          ) : (() => {
            let globalNum = 0;
            return event.tickets.map((ticket) => (
              <div key={ticket.ticket_reference} style={{ marginTop: 12 }}>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 13, marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, color: "#00703c" }}>{ticket.ticket_reference}</span>
                  {ticket.package && <span style={{ background: "#e8f5e9", color: "#00703c", padding: "1px 8px", borderRadius: 4, fontSize: 12 }}>{ticket.package}</span>}
                  <span>Booked by: <strong>{ticket.booker_name}</strong> — {ticket.booker_email}{ticket.booker_phone ? ` · ${ticket.booker_phone}` : ""}</span>
                  <span>${ticket.total_amount.toFixed(2)} AUD · {ticket.purchased_at}</span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead style={S.tblHead}>
                    <tr>
                      <th style={S.tblTh}>#</th>
                      <th style={S.tblTh}>Name</th>
                      <th style={S.tblTh}>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookerAttendees(ticket).map((a, i) => {
                      globalNum += 1;
                      return (
                        <tr key={i}>
                          <td style={S.tblTd}>{globalNum}</td>
                          <td style={S.tblTd}>{a.first_name} {a.last_name}</td>
                          <td style={S.tblTd}>{a.email || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ));
          })()}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: 0 }}>📋 Event Attendees Report</h3>
        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
          {report.length} events · {totalTickets} tickets sold · ${totalRevenue.toFixed(2)} AUD
        </p>
      </div>

      <div style={{ marginBottom: 28 }}>
        <h4 style={{ margin: "0 0 12px", color: "#1d4ed8", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: 6, padding: "2px 10px", fontSize: 13 }}>Upcoming</span>
          <span style={{ fontWeight: 400, fontSize: 13, color: "#64748b" }}>{upcomingEvents.length} event{upcomingEvents.length !== 1 ? "s" : ""}</span>
        </h4>
        {upcomingEvents.length === 0 ? <p style={{ color: "#94a3b8", fontSize: 13 }}>No upcoming events.</p> : upcomingEvents.map(renderEvent)}
      </div>

      <div>
        <h4 style={{ margin: "0 0 12px", color: "#64748b", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 6, padding: "2px 10px", fontSize: 13 }}>Past</span>
          <span style={{ fontWeight: 400, fontSize: 13, color: "#94a3b8" }}>{pastEvents.length} event{pastEvents.length !== 1 ? "s" : ""}</span>
        </h4>
        {pastEvents.length === 0 ? <p style={{ color: "#94a3b8", fontSize: 13 }}>No past events.</p> : pastEvents.map(renderEvent)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main LeaderPortal
// ─────────────────────────────────────────────────────────────────────────────
const ROLE_TABS = {
  admin:           ["members", "welfare", "payments", "events"],
  welfare_manager: ["welfare", "payments"],
  events_manager:  ["events"],
};

const TAB_LABELS = {
  members:  "👥 Members",
  welfare:  "🤝 Welfare",
  payments: "💳 Payments",
  events:   "📋 Events",
};

export default function LeaderPortal() {
  const [role,       setRole]       = useState(null);
  const [activeTab,  setActiveTab]  = useState(null);
  const [welfareTab, setWelfareTab] = useState("members");
  const [loading,    setLoading]    = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("members/dashboard/")
      .then((r) => {
        const memberRole = r.data?.role;
        if (!memberRole || memberRole === "member") {
          setRole("member");
        } else {
          setRole(memberRole);
          const tabs = ROLE_TABS[memberRole] || [];
          setActiveTab(tabs[0] || null);
        }
        setLoading(false);
      })
      .catch(() => { navigate("/login"); });
  }, [navigate]);

  if (loading) return <p style={{ padding: 40, fontFamily: "sans-serif", color: "#64748b" }}>Loading…</p>;

  if (role === "member") {
    return (
      <div style={S.page}>
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ fontSize: 48 }}>🔒</div>
          <h2>Access Restricted</h2>
          <p style={{ color: "#64748b" }}>The Leader Portal is only available to community leaders.<br />Contact an administrator if you believe you should have access.</p>
          <button onClick={() => navigate("/dashboard")} style={{ ...S.btn("#00703c"), marginTop: 16 }}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const allowedTabs = ROLE_TABS[role] || [];

  return (
    <div style={S.page}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ margin: 0, color: "#00703c" }}>🛡 Leader Portal</h2>
        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14, textTransform: "capitalize" }}>
          Signed in as: <strong>{role.replace("_", " ")}</strong>
        </p>
      </div>

      <div style={S.tabBar}>
        {allowedTabs.map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={S.tab(activeTab === tab)}>
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {activeTab === "members"  && <MembersTab />}
      {activeTab === "payments" && <PaymentsTab />}
      {activeTab === "events"   && <EventAttendeesTab />}

      {activeTab === "welfare" && (
        <div>
          <div style={{ ...S.tabBar, marginBottom: 20 }}>
            <button onClick={() => setWelfareTab("members")}        style={S.subTab(welfareTab === "members")}>Welfare Members</button>
            <button onClick={() => setWelfareTab("reimbursements")} style={S.subTab(welfareTab === "reimbursements")}>Reimbursements</button>
          </div>
          {welfareTab === "members"        && <WelfareMembersTab />}
          {welfareTab === "reimbursements" && <ReimbursementsTab />}
        </div>
      )}
    </div>
  );
}